/* ────────────────────────────────────────────────────────────────────────────
   Interview Report — ViewModel + Adapter layer
   ----------------------------------------------------------------------------
   Decouples the redesigned report UI from the raw `/get-interview/` response
   shape (InterviewSerializer). Components bind to `ReportViewModel` ONLY; when
   the backend contract changes, or richer evaluation data is added (confidence,
   coverage, objective health, evidence quality…), we update this adapter alone.

   HARD RULE: this adapter NEVER fabricates metrics. Every field is either taken
   verbatim from the API or derived by an explicit, honest computation over real
   values (e.g. a score band from a real score, an elapsed-seconds offset from
   two real timestamps). Absent data becomes `null`/`[]` so the UI can hide the
   section gracefully — it must never render placeholder analytics as if real.
   ──────────────────────────────────────────────────────────────────────────── */
import { buildProctoringV2VM } from './proctoring-v2/adapter';
export function scoreBand(pct) {
    if (pct == null || Number.isNaN(pct))
        return { tone: 'neutral', label: 'Not scored' };
    if (pct >= 80)
        return { tone: 'success', label: 'Strong' };
    if (pct >= 60)
        return { tone: 'warning', label: 'Moderate' };
    return { tone: 'danger', label: 'Needs work' };
}
// ── helpers ──────────────────────────────────────────────────────────────────
const asArray = (v) => (Array.isArray(v) ? v : []);
const asNum = (v) => v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v);
const pctOf = (score, denom) => {
    if (score == null || denom == null || denom === 0)
        return null;
    return Math.round((score / denom) * 100);
};
let _uid = 0;
const uid = (prefix) => `${prefix}-${_uid++}`;
const parseTime = (v) => {
    if (!v)
        return null;
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? null : t;
};
// Map a Transcript.proctoring_insight dict → the per-answer VM (or null).
const mapInsight = (raw) => {
    if (!raw || typeof raw !== 'object' || !Object.keys(raw).length)
        return null;
    const integ = raw.integrity || {};
    const conf = raw.confidence || {};
    const attrs = conf.attributes || {};
    const win = raw.answer_window || {};
    return {
        brief: raw.brief || '',
        risk: integ.risk || null,
        flags: Array.isArray(integ.flags) ? integ.flags : [],
        confidenceBand: conf.band || null,
        confidenceScore: conf.score === null || conf.score === undefined ? null : Number(conf.score),
        attention: attrs.attentiveness === null || attrs.attentiveness === undefined
            ? null
            : Number(attrs.attentiveness),
        windowStart: win.start === undefined ? null : Number(win.start),
        windowEnd: win.end === undefined ? null : Number(win.end),
        durationSec: win.duration_sec === undefined ? null : Number(win.duration_sec),
        eventsCount: Array.isArray(integ.events) ? integ.events.length : 0,
    };
};
// Friendly names for the integrity flag types the backend emits.
const FLAG_LABELS = {
    looking_down: 'Looking down',
    looking_away: 'Looking away',
    eye_movement: 'Gaze off-screen',
    multiple_faces: 'Multiple faces',
    disappear_from_cam: 'Face not visible',
    hand_near_face: 'Hand near face',
    eyes_closed: 'Eyes closed',
    slouching: 'Slouching',
    talking: 'Mouth movement',
};
const PATTERN_TONE = {
    strong: 'danger',
    moderate: 'warning',
    weak: 'neutral',
};
const OVERALL_TONE = {
    high: 'danger',
    medium: 'warning',
    low: 'success',
};
const mapPatterns = (raw) => asArray(raw).map((p) => {
    var _a, _b, _c, _d;
    const strength = (['strong', 'moderate', 'weak'].includes(p === null || p === void 0 ? void 0 : p.strength)
        ? p.strength
        : 'weak');
    return {
        key: String((_a = p === null || p === void 0 ? void 0 : p.key) !== null && _a !== void 0 ? _a : ''),
        headline: String((_b = p === null || p === void 0 ? void 0 : p.headline) !== null && _b !== void 0 ? _b : ''),
        strength,
        numbers: String((_c = p === null || p === void 0 ? void 0 : p.numbers) !== null && _c !== void 0 ? _c : ''),
        looksLike: String((_d = p === null || p === void 0 ? void 0 : p.looks_like) !== null && _d !== void 0 ? _d : ''),
        at: asArray(p === null || p === void 0 ? void 0 : p.at)
            .map((t) => Number(t))
            .filter((t) => Number.isFinite(t)),
        tone: PATTERN_TONE[strength] || 'neutral',
    };
})
    // Drop anything that lost its words in transit rather than rendering an
    // empty card, which reads as a finding with nothing behind it.
    .filter((p) => p.headline && p.numbers);
/** Scenario ids whose difference a camera cannot resolve: a phone, a tablet, a
 *  second screen and a printed page in the same place are the same pixels. They
 *  are grouped into one line for that reason, not for brevity — listing seven
 *  and then weakening all seven reads as noise and invites over-reading. */
const PLACEMENT_IDS = new Set(['1.1', '1.2', '1.3', '1.4', '1.7', '3', '4', '5', '56']);
const mapScenario = (raw) => {
    var _a, _b, _c;
    return ({
        id: String((_a = raw === null || raw === void 0 ? void 0 : raw.id) !== null && _a !== void 0 ? _a : ''),
        title: String((_b = raw === null || raw === void 0 ? void 0 : raw.title) !== null && _b !== void 0 ? _b : ''),
        short: String((raw === null || raw === void 0 ? void 0 : raw.short) || (raw === null || raw === void 0 ? void 0 : raw.title) || ''),
        confidence: (['low', 'medium', 'high'].includes(raw === null || raw === void 0 ? void 0 : raw.confidence)
            ? raw.confidence
            : 'medium'),
        why: String((_c = raw === null || raw === void 0 ? void 0 : raw.why) !== null && _c !== void 0 ? _c : ''),
    });
};
const mapScenarios = (raw) => {
    if (!raw)
        return null;
    const consistent = asArray(raw.consistent).map(mapScenario).filter((s) => s.id && s.title);
    const ruledOut = asArray(raw.ruled_out).map(mapScenario).filter((s) => s.id && s.title);
    const caveats = asArray(raw.weakened)
        .map((w) => { var _a; return String((_a = w === null || w === void 0 ? void 0 : w.why) !== null && _a !== void 0 ? _a : ''); })
        .filter(Boolean);
    if (!consistent.length && !ruledOut.length)
        return null;
    return {
        placements: consistent.filter((s) => PLACEMENT_IDS.has(s.id)),
        others: consistent.filter((s) => !PLACEMENT_IDS.has(s.id)),
        ruledOut,
        caveats,
    };
};
/** The interpretation for one span.
 *
 *  Deliberately hedged, and identically to everywhere else: gaze clustering
 *  detects a consistent off-axis attention target, so this says "consistent
 *  with consulting something off-screen" and never names an object. Direction
 *  is included because it is what a reviewer verifies against the video.
 */
const meaningOf = (e, hasPlacements) => {
    switch (e.type) {
        case 'looking_away':
        case 'looking_down':
        case 'eye_movement':
            // Only when the report as a whole found a placement pattern. A single
            // glance in an otherwise unremarkable interview means nothing, and
            // labelling it "consistent with consulting something" would manufacture
            // suspicion out of one ordinary movement.
            return hasPlacements ? 'consistent with consulting something off-screen' : null;
        case 'disappear_from_cam':
            return 'the camera was blocked, or the face left the frame';
        case 'multiple_faces':
            return 'another person was visible on camera';
        case 'hand_near_face':
            return 'a hand reached the face';
        default:
            // `talking`, `eyes_closed` and anything new: measurements, not evidence.
            return null;
    }
};
const HEADLINE = {
    high: { label: 'Review', sub: 'patterns worth checking', tone: 'danger' },
    medium: { label: 'Worth a look', sub: 'at the flagged moments', tone: 'warning' },
    low: { label: 'Clear', sub: 'nothing stood out', tone: 'success' },
};
/** The tile's headline, from the rollup where there is one.
 *
 *  Falls back to the old score band for reports generated before the pattern
 *  layer — those have nothing better, and showing "—" would hide a figure that
 *  is at least what the report was written against at the time. */
const integrityHeadlineOf = (overall, score) => {
    if (overall) {
        const h = HEADLINE[overall.level] || HEADLINE.low;
        return {
            integrityHeadline: h.label,
            integrityHeadlineSub: overall.coverageCapped ? 'coverage was incomplete' : h.sub,
            integrityHeadlineTone: overall.coverageCapped ? 'neutral' : h.tone,
        };
    }
    if (score == null) {
        return { integrityHeadline: null, integrityHeadlineSub: null, integrityHeadlineTone: 'neutral' };
    }
    return {
        integrityHeadline: `${score}/100`,
        integrityHeadlineSub: score >= 80 ? 'Good' : score >= 55 ? 'Fair' : 'Poor',
        integrityHeadlineTone: score >= 80 ? 'success' : score >= 55 ? 'warning' : 'danger',
    };
};
const mapPatternOverall = (raw) => {
    var _a, _b, _c;
    if (!raw || !raw.level)
        return null;
    const level = (['high', 'medium', 'low'].includes(raw.level)
        ? raw.level
        : 'low');
    return {
        level,
        line: String((_a = raw.line) !== null && _a !== void 0 ? _a : ''),
        strong: (_b = asNum(raw.strong)) !== null && _b !== void 0 ? _b : 0,
        moderate: (_c = asNum(raw.moderate)) !== null && _c !== void 0 ? _c : 0,
        coverageCapped: !!raw.coverage_capped,
        reasons: asArray(raw.reasons).map((r) => String(r)),
        tone: OVERALL_TONE[level] || 'neutral',
    };
};
const attentionSignal = (attn) => {
    let value = 'Not measured';
    let tone = 'neutral';
    if (attn != null && !Number.isNaN(attn)) {
        if (attn >= 0.75) {
            value = 'Highly focused';
            tone = 'success';
        }
        else if (attn >= 0.55) {
            value = 'Attentive';
            tone = 'success';
        }
        else if (attn >= 0.35) {
            value = 'Wavering';
            tone = 'warning';
        }
        else {
            value = 'Distracted';
            tone = 'danger';
        }
    }
    return { key: 'attention', label: 'Attention', value, tone };
};
const confidenceSignal = (band) => {
    const b = (band || '').toLowerCase();
    let value = 'Not measured';
    let tone = 'neutral';
    if (b === 'high') {
        value = 'High';
        tone = 'success';
    }
    else if (b === 'moderate-high') {
        value = 'Moderate-high';
        tone = 'success';
    }
    else if (b === 'moderate') {
        value = 'Moderate';
        tone = 'warning';
    }
    else if (b === 'low') {
        value = 'Low';
        tone = 'danger';
    }
    else if (b) {
        value = b.replace(/-/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
        tone = 'neutral';
    }
    return { key: 'confidence', label: 'Confidence', value, tone };
};
const integritySignal = (risk, flags) => {
    const r = (risk || '').toLowerCase();
    const flagText = (flags || [])
        .map((f) => FLAG_LABELS[f] || f.replace(/_/g, ' '))
        .slice(0, 2)
        .join(', ');
    let value;
    let tone;
    if (r === 'high') {
        value = 'Review needed';
        tone = 'danger';
    }
    else if (r === 'medium') {
        value = 'Minor flags';
        tone = 'warning';
    }
    else {
        value = flagText ? 'Minor flags' : 'Clear';
        tone = flagText ? 'warning' : 'success';
    }
    if (flagText && value !== 'Clear')
        value = `${value} · ${flagText}`;
    return { key: 'integrity', label: 'Integrity', value, tone };
};
// Shared by the video overlay (time-synced) and the transcript (per-entry) —
// same three plain-English signals from the same raw insight either way.
export const signalsFromInsight = (ins) => [
    attentionSignal(ins.attention),
    confidenceSignal(ins.confidenceBand),
    integritySignal(ins.risk, ins.flags),
];
// Build per-answer signal windows from transcript insights that carry a time
// window. Used by the video overlay to show what the candidate was doing while
// answering the question playing at the current timestamp.
export const buildAnswerSignals = (transcript) => transcript
    .map((t) => t.proctoringInsight)
    .filter((ins) => !!ins && ins.windowStart != null && ins.windowEnd != null && ins.windowEnd > ins.windowStart)
    .map((ins) => ({
    start: ins.windowStart,
    end: ins.windowEnd,
    signals: signalsFromInsight(ins),
}))
    .sort((a, b) => a.start - b.start);
const fmtElapsed = (ms) => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}m ${s.toString().padStart(2, '0')}s`;
};
const mmss = (sec) => {
    const s = Math.max(0, Math.floor(sec));
    return `${Math.floor(s / 60)
        .toString()
        .padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
};
const statusMeta = (status) => {
    switch ((status || '').toLowerCase()) {
        case 'pass':
            return { label: 'Passed', tone: 'success' };
        case 'fail':
            return { label: 'Rejected', tone: 'danger' };
        case 'completed':
            return { label: 'Completed', tone: 'neutral' };
        case 'in-progress':
            return { label: 'In progress', tone: 'warning' };
        case 'expired':
            return { label: 'Expired', tone: 'neutral' };
        case 'cancelled':
            return { label: 'Cancelled', tone: 'neutral' };
        case 'error':
            return { label: 'Processing error', tone: 'danger' };
        default:
            return { label: status || 'Scheduled', tone: 'neutral' };
    }
};
// Recommendation label derived HONESTLY from the backend Pass/Fail + score. We do
// not invent a hiring scale the backend doesn't have — this is a readable framing
// of the two real signals (status + final_score).
//
// Wording is deliberately screening-stage ("Shortlisted"/"Rejected") rather than
// hiring-decision ("Hire"/"No Hire"): the AI interview gates who advances, it
// does not make the offer. Exported so the mapping can be asserted directly —
// it is the one place this terminology is defined for both the dashboard and
// the PDF exports.
export const recommendationLabel = (status, score) => {
    if (status === 'Pass')
        return score != null && score >= 85 ? 'Shortlisted — strong' : 'Shortlisted';
    if (status === 'Fail')
        return score != null && score >= 45 ? 'Rejected — borderline' : 'Rejected';
    return 'Pending evaluation';
};
// ── main builder ───────────────────────────────────────────────────────────
export function buildReportViewModel(interview, opts = {}) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    _uid = 0;
    const iv = interview || {};
    const jd = iv.jd || {};
    const report = iv.final_report || null;
    const hasReport = !!report;
    // ── timeline anchor for video ↔ transcript sync (real timestamps only) ──────
    const startMs = (_a = parseTime(iv.started_at)) !== null && _a !== void 0 ? _a : parseTime(iv.valid_from);
    const endMs = parseTime(iv.ended_at);
    const canSyncVideo = !!iv.recording_url && startMs != null;
    const offsetOf = (ts) => {
        if (!canSyncVideo)
            return null;
        const t = parseTime(ts);
        if (t == null || startMs == null)
            return null;
        return Math.max(0, Math.round((t - startMs) / 1000));
    };
    // ── meta ────────────────────────────────────────────────────────────────
    const userType = opts.userType || {};
    const recDomain = opts.recruiterDomain || '';
    const profileId = userType.staffing_candidate_id || userType.candidate_profile_id;
    const candidateProfileHref = iv.target_service && profileId
        ? `${recDomain}/${userType.staffing_candidate_id ? 'my-' : ''}candidate-profile?id=${profileId}`
        : null;
    const roleHref = iv.target_service
        ? jd.job_reference_id
            ? `${recDomain}/job-post-details?id=${jd.job_reference_id}`
            : null
        : jd.id
            ? `/job-description/${jd.id}`
            : null;
    const stMeta = statusMeta(iv.status);
    const meta = {
        candidateName: iv.interviewee_name || 'Candidate',
        candidateEmail: iv.interviewee_email || '',
        candidatePhone: userType.mobile_number || null,
        candidateProfileHref,
        role: jd.title || iv.title || null,
        roleHref,
        interviewType: iv.interview_type || null,
        interviewLevel: jd.interview_level || iv.interview_level || null,
        // Prefer the actual start time so this matches the "Start Date & Time"
        // shown in the interviews list — `valid_from` is only the scheduled slot,
        // which can differ from when the candidate actually started.
        interviewDate: iv.started_at || iv.valid_from || null,
        scheduledDurationMin: asNum((_b = jd.interview_duration) !== null && _b !== void 0 ? _b : iv.interview_duration),
        actualDurationLabel: startMs != null && endMs != null && endMs > startMs ? fmtElapsed(endMs - startMs) : null,
        status: iv.status || '',
        statusLabel: stMeta.label,
        statusTone: stMeta.tone,
        isComplete: ['pass', 'fail', 'completed'].includes((iv.status || '').toLowerCase()),
        recordingUrl: iv.recording_url || null,
        recordingDurationSec: asNum(iv.recording_duration_sec),
        takenBy: iv.interview_taken_by || null,
    };
    // ── overall score ─────────────────────────────────────────────────────────
    const finalScore = asNum(report === null || report === void 0 ? void 0 : report.final_score);
    const passingScore = asNum(report === null || report === void 0 ? void 0 : report.passing_score);
    const reportStatus = (report === null || report === void 0 ? void 0 : report.status) === 'Pass' || (report === null || report === void 0 ? void 0 : report.status) === 'Fail' ? report.status : null;
    const score = {
        value: finalScore,
        passingScore,
        status: reportStatus,
        recommendationLabel: recommendationLabel(reportStatus, finalScore),
        tone: reportStatus === 'Pass' ? 'success' : reportStatus === 'Fail' ? 'danger' : scoreBand(finalScore).tone,
    };
    // ── criteria ───────────────────────────────────────────────────────────────
    // Two report generations, told apart by `scoring_version`:
    //  * VA-28392 deterministic reports (scoring_version === 2) send explicit
    //    `max_score` / `earned_score` / `percentage`; `min_score` is the pass
    //    THRESHOLD (a percentage) and `met` is a real check. A criterion no
    //    objective assesses has `assessed: false` and no score at all.
    //  * legacy LLM reports: `min_score` is the MAX marks (weights summing to 100),
    //    NOT a threshold — so there is no honest met/not-met flag.
    const deterministic = asNum(report === null || report === void 0 ? void 0 : report.scoring_version) === 2;
    const criteria = asArray(report === null || report === void 0 ? void 0 : report.criteria_scores).map((c) => {
        var _a, _b, _c;
        if (deterministic) {
            const sc = asNum((_a = c.earned_score) !== null && _a !== void 0 ? _a : c.score);
            const target = asNum(c.max_score);
            const pct = (_b = asNum(c.percentage)) !== null && _b !== void 0 ? _b : pctOf(sc, target);
            return {
                id: uid('crit'),
                title: c.title || 'Criterion',
                score: sc,
                target,
                pct,
                reason: c.reason || null,
                met: typeof c.met === 'boolean' ? c.met : null,
                band: scoreBand(pct),
                isCommunication: /communicat/i.test(c.title || ''),
                threshold: asNum((_c = c.threshold) !== null && _c !== void 0 ? _c : c.min_score),
                assessed: c.assessed !== false,
            };
        }
        const sc = asNum(c.score);
        const target = asNum(c.min_score);
        const pct = pctOf(sc, target);
        return {
            id: uid('crit'),
            title: c.title || 'Criterion',
            score: sc,
            target,
            pct,
            reason: c.reason || null,
            met: null,
            band: scoreBand(pct),
            isCommunication: /communicat/i.test(c.title || ''),
        };
    });
    // ── evaluated question mapper (shared) ─────────────────────────────────────
    const mapEvalQ = (q) => {
        var _a, _b, _c;
        const sc = asNum((_a = q.earned_score) !== null && _a !== void 0 ? _a : q.score);
        const outOf = asNum((_c = (_b = q.max_score) !== null && _b !== void 0 ? _b : q.out_of) !== null && _c !== void 0 ? _c : q.outOf);
        return {
            status: q.answer_status || null,
            coverageLabel: q.coverage_label || null,
            source: q.source === 'dynamic' ? 'dynamic' : q.source === 'predefined' ? 'predefined' : null,
            id: uid('eq'),
            question: q.question || q.text || q.title || null,
            answer: q.answer || null,
            code: q.code || q.code_snippet || null,
            language: q.language || null,
            reason: q.reason || q.explanation || q.ai_feedback || null,
            score: sc,
            outOf,
            pct: pctOf(sc, outOf),
        };
    };
    // ── objectives (from interview_structure_scores, coding merged server-side) ─
    const structure = asArray(report === null || report === void 0 ? void 0 : report.interview_structure_scores);
    const codingMerged = asArray(iv.interview_structure_with_coding);
    const codingBySubTitle = new Map();
    codingMerged.forEach((o) => asArray(o.sub_objectives).forEach((s) => {
        const key = (s.title || '').trim().toLowerCase();
        if (key && asArray(s.coding_questions).length)
            codingBySubTitle.set(key, asArray(s.coding_questions));
    }));
    const objectives = structure.map((o, i) => {
        const subs = asArray(o.sub_objectives).map((s) => {
            // Legacy `score` is the sub-objective's MAX marks, not a percentage; the
            // deterministic report carries the real earned percentage.
            const subScore = deterministic ? asNum(s.percentage) : asNum(s.score);
            const subCrit = asArray(s.evaluation_criteria).map((c) => {
                var _a;
                if (deterministic) {
                    // Rubric row (O4): its own rating, as a percentage, vs its own threshold.
                    const pct = asNum(c.percentage);
                    return {
                        id: uid('scrit'),
                        title: c.title || 'Criterion',
                        score: pct,
                        target: pct != null ? 100 : null,
                        pct,
                        reason: c.reason || null,
                        met: typeof c.met === 'boolean' ? c.met : null,
                        band: scoreBand(pct),
                        isCommunication: /communicat/i.test(c.title || ''),
                        threshold: asNum((_a = c.threshold) !== null && _a !== void 0 ? _a : c.min_score),
                        evidenced: c.evidenced !== false,
                    };
                }
                const sc = asNum(c.score);
                const target = asNum(c.min_score);
                const pct = pctOf(sc, target);
                return {
                    id: uid('scrit'),
                    title: c.title || 'Criterion',
                    score: sc,
                    target,
                    pct,
                    reason: c.reason || null,
                    met: null,
                    band: scoreBand(pct),
                    isCommunication: /communicat/i.test(c.title || ''),
                };
            });
            const codingQs = (codingBySubTitle.get((s.title || '').trim().toLowerCase()) || []).map(mapEvalQ);
            return {
                id: uid('sub'),
                title: s.title || 'Sub-objective',
                score: subScore,
                earned: deterministic ? asNum(s.earned_score) : null,
                max: deterministic ? asNum(s.max_score) : null,
                weight: asNum(s.weight),
                band: scoreBand(subScore),
                criteria: subCrit,
                questions: asArray(s.evaluated_questions).map(mapEvalQ),
                codingQuestions: codingQs,
            };
        });
        const objScore = deterministic ? asNum(o.percentage) : asNum(o.score);
        return {
            id: uid('obj'),
            index: i,
            title: o.title || 'Objective',
            score: objScore,
            earned: deterministic ? asNum(o.earned_score) : null,
            max: deterministic ? asNum(o.max_score) : null,
            weight: asNum(o.weight),
            band: scoreBand(objScore),
            subObjectives: subs,
            questionCount: subs.reduce((n, s) => n + s.questions.length + s.codingQuestions.length, 0),
        };
    });
    // ── transcript ─────────────────────────────────────────────────────────────
    const candidateName = meta.candidateName;
    const transcript = asArray(iv.transcript).map((m) => {
        const isInterviewer = m.sender === 'interviewer';
        return {
            id: uid('tr'),
            speaker: isInterviewer ? 'interviewer' : 'candidate',
            speakerName: isInterviewer ? 'Interview Bot' : candidateName,
            text: m.text || '',
            timestamp: m.timestamp || null,
            timeLabel: m.timestamp ? new Date(m.timestamp).toLocaleTimeString() : null,
            videoOffsetSec: offsetOf(m.timestamp),
            type: m.type || null,
            score: asNum(m.score),
            reason: m.reason || null,
            isCoding: !!m.is_coding_question,
            code: m.code_snippet || null,
            proctoringInsight: mapInsight(m.proctoring_insight),
        };
    });
    // ── timeline (derived from real transcript timestamps — no fabricated events) ─
    const tlStart = startMs !== null && startMs !== void 0 ? startMs : parseTime((_c = asArray(iv.transcript)[0]) === null || _c === void 0 ? void 0 : _c.timestamp);
    const timeline = [];
    if (tlStart != null) {
        asArray(iv.transcript).forEach((m) => {
            const t = parseTime(m.timestamp);
            if (t == null)
                return;
            const elapsed = Math.max(0, (t - tlStart) / 1000);
            if (m.type === 'greeting') {
                timeline.push({ id: uid('tl'), label: mmss(elapsed), text: 'Interview started', kind: 'start' });
            }
            else if (m.type === 'question') {
                // Markdown emphasis the model wrote into the question is shown raw here.
                const txt = (m.text || '').replace(/\*\*|__/g, '').trim();
                timeline.push({
                    id: uid('tl'),
                    label: mmss(elapsed),
                    // Full text — the view clamps it and lets the reader expand it.
                    text: txt || 'Question asked',
                    kind: m.is_coding_question ? 'coding' : 'question',
                });
            }
        });
    }
    // ── evidence (primary: evaluated questions inside objectives; carries the
    //    objective/sub-objective mapping. supplemented by scored transcript
    //    answers that aren't already represented). ──────────────────────────────
    const evidence = [];
    objectives.forEach((o) => o.subObjectives.forEach((s) => {
        [...s.questions, ...s.codingQuestions].forEach((q) => {
            var _a;
            const isCoding = !!q.code;
            evidence.push({
                id: uid('ev'),
                question: q.question,
                response: q.answer || q.code,
                code: q.code,
                language: q.language,
                evaluation: q.reason,
                score: q.score,
                outOf: q.outOf,
                pct: q.pct,
                band: scoreBand(q.pct),
                objectiveTitle: o.title,
                subObjectiveTitle: s.title,
                criterionTitle: ((_a = s.criteria[0]) === null || _a === void 0 ? void 0 : _a.title) || null,
                timestamp: null,
                videoOffsetSec: null,
                isCoding,
                source: isCoding ? 'coding' : 'objective',
            });
        });
    }));
    // Supplement with scored candidate answers from the transcript (real per-answer
    // scores) — only when objective-level evidence is thin, so we never duplicate.
    if (evidence.length === 0) {
        transcript.forEach((t, i) => {
            var _a;
            if (t.speaker === 'candidate' && (t.score != null || t.reason)) {
                const q = ((_a = transcript[i - 1]) === null || _a === void 0 ? void 0 : _a.speaker) === 'interviewer' ? transcript[i - 1] : null;
                evidence.push({
                    id: uid('ev'),
                    question: (q === null || q === void 0 ? void 0 : q.text) || null,
                    response: t.text,
                    code: t.code,
                    language: null,
                    evaluation: t.reason,
                    score: t.score,
                    outOf: t.score != null ? 100 : null,
                    pct: t.score,
                    band: scoreBand(t.score),
                    objectiveTitle: null,
                    subObjectiveTitle: null,
                    criterionTitle: null,
                    timestamp: t.timestamp,
                    videoOffsetSec: t.videoOffsetSec,
                    isCoding: t.isCoding,
                    source: 'transcript',
                });
            }
        });
    }
    // ── coding items (dedicated coding tab) ────────────────────────────────────
    const coding = [];
    asArray(iv.coding_que_and_ans_data).forEach((q) => {
        var _a;
        const sc = asNum(q.score);
        const outOf = asNum((_a = q.out_of) !== null && _a !== void 0 ? _a : q.outOf);
        coding.push({
            id: uid('code'),
            title: q.title || q.sub_objective_title || null,
            question: q.question || q.objective_text || q.description || null,
            // VA-28013: the code, and NEVER `answer`.
            //
            // The two interview modes store a coding submission under DIFFERENT
            // KEYS, and `answer` means different things in each:
            //
            //   cost-optimized  submitted_code = the code
            //                   answer         = the SPOKEN walkthrough
            //   realtime        code_snippet   = the code
            //                   answer         = the code (duplicated)
            //
            // The old chain `code || code_snippet || answer` therefore worked for
            // realtime and fell through to prose for cost-optimized: the Coding tab
            // printed "So I would walk through it step by step…" under the heading
            // "Candidate Code", or nothing at all when there was no walkthrough
            // either. Three failure modes in QA's audit, one cause.
            //
            // Dropping the `answer` fallback loses nothing: realtime sets
            // `code_snippet` in the same branch that sets `answer`, guarded on the
            // same value, so whenever `answer` held real code `code_snippet` holds
            // it too.
            code: q.submitted_code || q.code_snippet || q.code || null,
            walkthrough: q.discussion || null,
            submittedWithCode: typeof q.submitted_with_code === 'boolean' ? q.submitted_with_code : null,
            language: q.language || null,
            evaluation: q.reason || q.explanation || null,
            score: sc,
            outOf,
            pct: pctOf(sc, outOf),
            band: scoreBand(pctOf(sc, outOf)),
            syntaxValid: typeof q.syntax_valid === 'boolean' ? q.syntax_valid : null,
            testCasesPassed: asNum(q.test_cases_passed),
            totalTestCases: asNum(q.total_test_cases),
            aiFeedback: q.ai_feedback || null,
            objectiveTitle: q.objective_title || null,
            subObjectiveTitle: q.sub_objective_title || null,
        });
    });
    // Fallback: coding entries embedded in the transcript.
    if (coding.length === 0) {
        transcript.forEach((t) => {
            if (t.isCoding && t.code) {
                coding.push({
                    id: uid('code'),
                    title: null,
                    question: null,
                    code: t.code,
                    walkthrough: null,
                    submittedWithCode: null,
                    language: null,
                    evaluation: t.reason,
                    score: t.score,
                    outOf: t.score != null ? 100 : null,
                    pct: t.score,
                    band: scoreBand(t.score),
                    syntaxValid: null,
                    testCasesPassed: null,
                    totalTestCases: null,
                    aiFeedback: null,
                    objectiveTitle: null,
                    subObjectiveTitle: null,
                });
            }
        });
    }
    // ── behaviour (soft-skill criteria surfaced honestly from real criteria) ────
    const behaviourRe = /communicat|behav|attitude|confiden|clarity|articul|interpersonal|collaborat/i;
    const behaviour = criteria.filter((c) => behaviourRe.test(c.title));
    // ── proctoring (structured cards from real counts) ──────────────────────────
    const cds = iv.cheating_detector_summary || (report === null || report === void 0 ? void 0 : report.cheating_detector_summary) || {};
    const proctorDefs = [
        { key: 'multiple_faces', label: 'Multiple Faces', field: 'multiple_faces_count' },
        { key: 'left_frame', label: 'Left Frame', field: 'disappear_from_cam_count' },
        { key: 'tab_switch', label: 'Tab Switches', field: 'total_tab_switch_count' },
        { key: 'camera_stopped', label: 'Camera Stops', field: 'camera_stopped_count' },
        { key: 'eye_movement', label: 'Eye Movements', field: 'eye_movement_count' },
        { key: 'head_movement', label: 'Head Movements', field: 'head_movement_count' },
    ];
    const proctorCards = proctorDefs
        .filter((d) => cds[d.field] !== undefined && cds[d.field] !== null)
        .map((d) => ({ key: d.key, label: d.label, value: Number(cds[d.field]) || 0 }));
    const summaryText = ((_d = report === null || report === void 0 ? void 0 : report.evaluation_of_cheating_detection) === null || _d === void 0 ? void 0 : _d.summary) || '';
    const summarySentences = summaryText
        ? summaryText.split(/(?<=[.!?])\s+/).filter((s) => s.trim())
        : [];
    // ── holistic engine outputs (post-interview batch) ──────────────────────────
    const overall = (report === null || report === void 0 ? void 0 : report.proctoring_overall) || {};
    const integrity = overall.integrity || {};
    const overallBody = overall.body_language || (report === null || report === void 0 ? void 0 : report.body_language_summary) || {};
    const overallConf = overall.confidence || (report === null || report === void 0 ? void 0 : report.confidence_summary) || {};
    const verdict = integrity.verdict || null;
    const VERDICT_META = {
        no_concern: { label: 'No integrity concerns', tone: 'success' },
        review: { label: 'Review recommended', tone: 'warning' },
        concern: { label: 'Integrity concern', tone: 'danger' },
    };
    const vMeta = (verdict && VERDICT_META[verdict]) || { label: '', tone: 'neutral' };
    // VA-28223 P3: the pattern findings, stored beside the correlated evidence.
    const rawEvidence = (report === null || report === void 0 ? void 0 : report.proctoring_evidence) || null;
    const patterns = mapPatterns(rawEvidence === null || rawEvidence === void 0 ? void 0 : rawEvidence.patterns);
    const patternOverall = mapPatternOverall(rawEvidence === null || rawEvidence === void 0 ? void 0 : rawEvidence.overall);
    const scenarios = mapScenarios(rawEvidence === null || rawEvidence === void 0 ? void 0 : rawEvidence.scenarios);
    const rawSummary = (report === null || report === void 0 ? void 0 : report.proctoring_summary) || null;
    const summary = rawSummary
        ? {
            text: String(rawSummary.text || ''),
            verdict: String(rawSummary.verdict || ''),
            reviewRequired: !!rawSummary.review_required,
            observedSec: Number(rawSummary.observed_sec || 0),
            lookAwaySec: Number(rawSummary.look_away_sec || 0),
            lookAwayShare: Number(rawSummary.look_away_share || 0),
            faceAbsentSec: Number(rawSummary.face_absent_sec || 0),
            directions: Object.entries(rawSummary.directions || {}).map(([name, d]) => ({
                name,
                seconds: Number((d === null || d === void 0 ? void 0 : d.seconds) || 0),
                count: Number((d === null || d === void 0 ? void 0 : d.count) || 0),
                longest: Number((d === null || d === void 0 ? void 0 : d.longest) || 0),
                longestAt: (d === null || d === void 0 ? void 0 : d.longest_at) != null ? Number(d.longest_at) : null,
            })),
            profile: Object.entries(((_e = rawSummary.profile) === null || _e === void 0 ? void 0 : _e.totals) || {}).map(([name, t]) => ({
                name,
                seconds: Number((t === null || t === void 0 ? void 0 : t.seconds) || 0),
                share: Number((t === null || t === void 0 ? void 0 : t.share) || 0),
            })),
            segments: asArray((_f = rawSummary.profile) === null || _f === void 0 ? void 0 : _f.segments).map((sg) => ({
                from: Number((sg === null || sg === void 0 ? void 0 : sg.from) || 0),
                to: Number((sg === null || sg === void 0 ? void 0 : sg.to) || 0),
                seconds: Number((sg === null || sg === void 0 ? void 0 : sg.seconds) || 0),
                direction: String((sg === null || sg === void 0 ? void 0 : sg.direction) || ''),
            })),
            centreLabel: String(((_g = rawSummary.profile) === null || _g === void 0 ? void 0 : _g.centre_label) || 'facing the screen'),
            concerns: asArray(rawSummary.concerns).map((c) => String(c)),
            limits: rawSummary.limits
                ? {
                    head: Number(rawSummary.limits.head || 0),
                    gaze: Number(rawSummary.limits.gaze || 0),
                    dwell: Number(rawSummary.limits.dwell || 0),
                }
                : null,
            calibrationWarning: rawSummary.calibration_warning
                ? String(rawSummary.calibration_warning)
                : null,
        }
        : null;
    const events = asArray(report === null || report === void 0 ? void 0 : report.proctoring_events).map((e) => ({
        type: e.type || 'event',
        label: e.label || e.type || 'Event',
        start: Number(e.start) || 0,
        end: Number(e.end) || 0,
        durationSec: Number(e.duration_sec) || 0,
        peakMagnitude: Number(e.peak_magnitude) || 0,
        unit: e.unit || '',
        severity: (['low', 'medium', 'high'].includes(e.severity) ? e.severity : 'low'),
        integrity: !!e.integrity,
        direction: e.direction ? String(e.direction) : null,
        meaning: null, // filled below, once the scenarios are known
    }));
    // VA-28265 Phase 11: the unified evidence feed, mapped straight through.
    //
    // Sorted defensively even though the backend sorts: the renderer walks this in
    // order to decide what is active at a given moment, and a single out-of-order
    // event makes the active-evidence panel flicker between two events.
    const evidenceTimeline = asArray(report === null || report === void 0 ? void 0 : report.evidence_timeline)
        .map((e) => {
        var _a, _b, _c, _d, _e;
        return ({
            id: String((_a = e.id) !== null && _a !== void 0 ? _a : ''),
            start: Number(e.start_time) || 0,
            end: Number(e.end_time) || 0,
            durationSec: Number(e.duration_sec) || 0,
            source: String((_b = e.source) !== null && _b !== void 0 ? _b : 'system'),
            type: String((_c = e.type) !== null && _c !== void 0 ? _c : 'unknown'),
            title: String((_d = e.title) !== null && _d !== void 0 ? _d : ''),
            description: String((_e = e.description) !== null && _e !== void 0 ? _e : ''),
            severity: (['low', 'medium', 'high'].includes(e.severity) ? e.severity : 'low'),
            integrity: !!e.integrity,
            questionId: e.question_id ? String(e.question_id) : null,
            relatedIds: asArray(e.related_event_ids).map((r) => String(r)),
            instant: !!(e.metadata && e.metadata.instant),
            metadata: (e.metadata || {}),
        });
    })
        .filter((e) => !!e.id)
        .sort((a, b) => a.start - b.start || a.source.localeCompare(b.source));
    const bodyLanguage = overallBody && Object.keys(overallBody).length
        ? {
            available: !!overallBody.available,
            posture: overallBody.posture || 'unknown',
            openness: overallBody.openness || 'unknown',
            fidget: overallBody.fidget_level || 'unknown',
        }
        : null;
    const confAttrs = (overallConf.attributes || {});
    const confidence = overallConf && (overallConf.overall_score != null || overallConf.band)
        ? {
            score: asNum(overallConf.overall_score),
            band: overallConf.band || null,
            trend: overallConf.trend || null,
            drivers: asArray(overallConf.drivers),
            attributes: Object.keys(confAttrs).map((k) => ({ key: k, value: Number(confAttrs[k]) || 0 })),
            disclaimer: overall.disclaimer ||
                'Confidence and body-language values are automated estimates for reviewer context only — not competency scores.',
        }
        : null;
    const layoutFit = asArray(report === null || report === void 0 ? void 0 : report.proctoring_layout_fit)
        .map((f) => ({
        from: Number(f === null || f === void 0 ? void 0 : f.from) || 0,
        to: Number(f === null || f === void 0 ? void 0 : f.to) || 0,
        A: Number(f === null || f === void 0 ? void 0 : f.A) || 0,
        B: Number(f === null || f === void 0 ? void 0 : f.B) || 0,
        C: Number(f === null || f === void 0 ? void 0 : f.C) || 0,
        D: Number(f === null || f === void 0 ? void 0 : f.D) || 0,
        residualPx: Number(f === null || f === void 0 ? void 0 : f.residual_px) || 0,
        frames: Number(f === null || f === void 0 ? void 0 : f.frames) || 0,
    }))
        // A fit with no scale is not a fit. Dropping it here means the overlay
        // falls back to the stored box rather than collapsing every landmark onto
        // a single point.
        .filter((f) => f.B !== 0 && f.D !== 0 && f.to > f.from);
    const capture = asArray(report === null || report === void 0 ? void 0 : report.proctoring_capture);
    // OV-1: sort by timestamp so findLayoutAt's binary search is correct and a
    // later transition (e.g. the code-editor PiP) can never be back-applied to the
    // interview's opening seconds. Rows should arrive monotonically, but a
    // reconnect/replay or out-of-order delivery could break the ordering the
    // binary search relies on.
    const layout = asArray(report === null || report === void 0 ? void 0 : report.proctoring_layout)
        .slice()
        .sort((a, b) => (Number(a === null || a === void 0 ? void 0 : a.t) || 0) - (Number(b === null || b === void 0 ? void 0 : b.t) || 0));
    const baseline = (report === null || report === void 0 ? void 0 : report.proctoring_baseline) || null;
    const series = asArray(report === null || report === void 0 ? void 0 : report.proctoring_series);
    // event-type → human label (shared by charts)
    const EVENT_LABELS = {
        looking_down: 'Looking Down',
        looking_away: 'Looking Away',
        eye_movement: 'Gaze Off-screen',
        multiple_faces: 'Multiple Faces',
        disappear_from_cam: 'Camera Missing',
        eyes_closed: 'Eyes Closed',
        talking: 'Mouth Movement',
        hand_near_face: 'Hand Near Face',
        slouching: 'Slouching',
    };
    const labelOf = (t) => EVENT_LABELS[t] || t.replace(/_/g, ' ');
    const GAZE_LABELS = {
        center: 'Center',
        left: 'Left',
        right: 'Right',
        up: 'Up',
        down: 'Down',
    };
    const rawCharts = overall.charts || {};
    const charts = {
        eventsByType: asArray(rawCharts.events_by_type).map((e) => ({
            type: e.type,
            label: labelOf(e.type),
            count: Number(e.count) || 0,
        })),
        severityDistribution: asArray(rawCharts.severity_distribution).map((s) => ({
            severity: s.severity,
            count: Number(s.count) || 0,
        })),
        riskBreakdown: asArray(rawCharts.risk_breakdown).map((r) => ({
            type: r.type,
            label: labelOf(r.type),
            severity: r.severity,
            pct: Number(r.pct) || 0,
        })),
        gazeDistribution: asArray(rawCharts.gaze_distribution).map((g) => ({
            dir: g.dir,
            label: GAZE_LABELS[g.dir] || g.dir,
            pct: Number(g.pct) || 0,
        })),
        confidenceTrend: asArray(rawCharts.confidence_trend).map((c) => ({
            t: Number(c.t) || 0,
            score: Number(c.score) || 0,
        })),
        facePresencePct: asNum(rawCharts.face_presence_pct),
        singlePersonPct: asNum(rawCharts.single_person_pct),
        totalEvents: Number(rawCharts.total_events) || events.length,
        eventCategories: Number(rawCharts.event_categories) || 0,
    };
    // VA-28223: attach the interpretation to each span, from the scenarios that
    // were derived from these very events. Composed here rather than in the
    // components so the player, the timeline and any future surface cannot drift
    // into describing the same second three different ways.
    const hasPlacements = !!((_h = scenarios === null || scenarios === void 0 ? void 0 : scenarios.placements) === null || _h === void 0 ? void 0 : _h.length);
    events.forEach((e) => {
        e.meaning = meaningOf(e, hasPlacements);
    });
    const integrityScore = asNum(integrity.score);
    const proctoring = Object.assign(Object.assign({ patterns,
        patternOverall,
        scenarios, evidence: evidenceTimeline, available: proctorCards.length > 0 ||
            summarySentences.length > 0 ||
            events.length > 0 ||
            // A report whose ONLY proctoring output is the evidence feed still has
            // proctoring — omitting this would hide the whole tab for it.
            evidenceTimeline.length > 0 ||
            !!verdict ||
            !!confidence, cards: proctorCards, summarySentences, totalFrames: (_j = asNum(cds.total_frames)) !== null && _j !== void 0 ? _j : asNum(overall.total_frames), verdict, verdictLabel: vMeta.label || null, verdictTone: vMeta.tone, verdictReasons: asArray(integrity.reasons), totalFlaggedSec: asNum(integrity.total_flagged_sec), reviewRecommended: !!integrity.review_recommended, integrityScore, integrityBand: integrityScore == null ? null : integrityScore >= 80 ? 'Good' : integrityScore >= 55 ? 'Fair' : 'Poor' }, integrityHeadlineOf(patternOverall, integrityScore)), { tabSwitches: asNum(cds.total_tab_switch_count), summary,
        events,
        bodyLanguage,
        confidence,
        capture,
        layout,
        layoutFit,
        baseline,
        series,
        charts });
    // ── counts (only populate what is genuinely derivable) ──────────────────────
    // VA-28038: every interviewer turn is labelled "question" in the transcript —
    // reprompts, clarifications and the goodbye included — so counting those rows
    // inflated the tile. Count real questions instead: the report's own coverage
    // (predefined questions covered + dynamic questions asked), else the
    // evaluated questions that carry an answer or a score, else the old count.
    const coverage = report === null || report === void 0 ? void 0 : report.coverage_summary;
    const coverageAsked = coverage && (asNum(coverage.covered) != null || asNum(coverage.dynamic_asked) != null)
        ? ((_k = asNum(coverage.covered)) !== null && _k !== void 0 ? _k : 0) + ((_l = asNum(coverage.dynamic_asked)) !== null && _l !== void 0 ? _l : 0)
        : null;
    const evaluatedAsked = objectives.reduce((n, o) => n +
        o.subObjectives.reduce((m, s) => m + s.questions.filter((q) => !!q.answer || q.score != null).length, 0), 0);
    const questionsAsked = coverageAsked !== null && coverageAsked !== void 0 ? coverageAsked : (evaluatedAsked || transcript.filter((t) => t.type === 'question').length);
    const subObjCount = objectives.reduce((n, o) => n + o.subObjectives.length, 0);
    const counts = {
        questionsAsked: questionsAsked || null,
        objectives: objectives.length || null,
        subObjectives: subObjCount || null,
        criteria: criteria.length || null,
        criteriaMet: null,
        evidence: evidence.length || null,
        codingChallenges: coding.length || null,
    };
    return {
        hasReport,
        meta,
        score,
        summary: {
            reason: (report === null || report === void 0 ? void 0 : report.reason) || null,
            strengths: asArray(report === null || report === void 0 ? void 0 : report.key_strengths).filter(Boolean),
            improvements: asArray(report === null || report === void 0 ? void 0 : report.areas_to_improve).filter(Boolean),
            narrativeStatus: (report === null || report === void 0 ? void 0 : report.narrative_status) === 'pending' || (report === null || report === void 0 ? void 0 : report.narrative_status) === 'ready' ||
                (report === null || report === void 0 ? void 0 : report.narrative_status) === 'failed'
                ? report.narrative_status
                : null,
        },
        counts,
        criteria,
        objectives,
        evidence,
        transcript,
        timeline,
        coding,
        behaviour,
        proctoring,
        proctoringV2: buildProctoringV2VM(iv, transcript),
    };
}
